import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ZweiFaktorEinrichtung from '../components/ZweiFaktorEinrichtung';
import './LoginPage.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface LoginPageProps {
  onLogin: (token: string, user: {
    benutzer_id?: number;
    name: string;
    email: string;
    rolle: string;
    tenant_id: number | null;
    tenant_slug: string | null;
    firmenname: string | null;
    primaerfarbe: string | null;
  }) => void;
}

interface MandantOption {
  slug: string;
  firmenname: string;
}

type Schritt = 'passwort' | 'code' | 'einrichten';

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [tenantSlug, setTenantSlug] = useState<string>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mandanten, setMandanten] = useState<MandantOption[]>([]);

  // 2FA-Schritt (Spec §5.5) – Zwischen-Token nur im State, nie in localStorage
  const [schritt, setSchritt] = useState<Schritt>('passwort');
  const [zwischenToken, setZwischenToken] = useState('');
  const [methode, setMethode] = useState<'totp' | 'email' | null>(null);
  const [emailMaskiert, setEmailMaskiert] = useState('');
  const [code, setCode] = useState('');
  const [geraetMerken, setGeraetMerken] = useState(false);
  const [backupModus, setBackupModus] = useState(false);
  const [resendSperre, setResendSperre] = useState(0);
  const [codeGesendet, setCodeGesendet] = useState<boolean | null>(null);

  useEffect(() => {
    const hostname = window.location.hostname;
    const hostSlug = hostname !== 'localhost' && hostname !== '127.0.0.1'
      ? hostname.split('.')[0] : '';
    axios.get(`${API_URL}/api/auth/mandanten-liste`)
      .then(res => {
        setMandanten(res.data);
        // Subdomain nur vorbelegen, wenn es den Mandanten gibt: sonst steht z.B. "erp"
        // (erp.quantum90.io) unsichtbar im State, das Dropdown zeigt "Firma wählen"
        // und jeder Login endet in 404 "Mandant nicht gefunden".
        if (hostSlug && res.data.some((m: MandantOption) => m.slug === hostSlug)) {
          setTenantSlug(hostSlug);
        }
      })
      .catch(() => {/* Fallback: Textfeld bleibt leer */});
  }, []);

  useEffect(() => {
    if (resendSperre <= 0) return;
    const t = setTimeout(() => setResendSperre(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendSperre]);

  const fehlerText = (err: any, fallback: string) => {
    const status = err.response?.status;
    const detail = err.response?.data?.detail;
    if (status === 423) return detail || 'Account gesperrt. Bitte später erneut versuchen.';
    if (status === 429) return detail || 'Zu viele Versuche. Bitte kurz warten.';
    return detail || fallback;
  };

  const abschliessen = (data: any) => {
    const { access_token, benutzer_id, name, email: userEmail, rolle, tenant_id, tenant_slug, firmenname, primaerfarbe, geraete_token } = data;
    if (geraete_token) localStorage.setItem('geraete_token', geraete_token);
    localStorage.setItem('auth_token', access_token);
    onLogin(access_token, {
      benutzer_id: typeof benutzer_id === 'number' ? benutzer_id : undefined,
      name,
      email: userEmail,
      rolle,
      tenant_id: tenant_id ?? null,
      tenant_slug: tenant_slug ?? null,
      firmenname: firmenname ?? null,
      primaerfarbe: primaerfarbe ?? null,
    });
  };

  const anmelden = async () => {
    const body: Record<string, string | undefined> = { email, passwort };
    if (tenantSlug.trim()) body.tenant_slug = tenantSlug.trim();
    const gt = localStorage.getItem('geraete_token');
    if (gt) body.geraete_token = gt;
    const res = await axios.post(`${API_URL}/api/auth/login`, body);
    const data = res.data;
    if (data.naechster_schritt === 'zwei_faktor') {
      // Server hat ein evtl. mitgeschicktes Geraete-Token nicht akzeptiert → weg damit
      if (gt) localStorage.removeItem('geraete_token');
      setZwischenToken(data.zwischen_token);
      setMethode(data.methode);
      setEmailMaskiert(data.email_maskiert || '');
      setCode('');
      setBackupModus(false);
      setCodeGesendet(data.methode === 'email' ? data.code_gesendet ?? null : null);
      setResendSperre(data.methode === 'email' && data.code_gesendet !== false ? 60 : 0);
      setSchritt('code');
      return;
    }
    if (data.naechster_schritt === 'zwei_faktor_einrichten') {
      setZwischenToken(data.zwischen_token);
      setSchritt('einrichten');
      return;
    }
    abschliessen(data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await anmelden();
    } catch (err: any) {
      setError(fehlerText(err, 'E-Mail oder Passwort falsch.'));
    } finally {
      setLoading(false);
    }
  };

  const handleCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/login/zwei-faktor`,
        { code, geraet_merken: geraetMerken },
        { headers: { Authorization: `Bearer ${zwischenToken}` } },
      );
      abschliessen(res.data);
    } catch (err: any) {
      // Maschinenlesbarer Code statt Textabgleich: ein falscher Code ist ebenfalls 401,
      // darf den Nutzer aber nicht zurueck auf die Passwort-Maske werfen.
      if (err.response?.status === 401 && err.response?.data?.fehler_code === 'zwischen_token_ungueltig') {
        setError('Zeit abgelaufen – bitte erneut anmelden.');
        setSchritt('passwort');
      } else {
        setError(fehlerText(err, 'Code ungültig.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const codeErneutSenden = async () => {
    setError('');
    try {
      await axios.post(`${API_URL}/api/auth/login/zwei-faktor/code-senden`, {},
        { headers: { Authorization: `Bearer ${zwischenToken}` } });
      setCodeGesendet(true);
      setResendSperre(60);
    } catch (err: any) {
      setError(fehlerText(err, 'Code konnte nicht gesendet werden.'));
    }
  };

  const nachEinrichtung = async () => {
    // Zugangsdaten sind noch im State: Login wiederholen → Server antwortet jetzt mit zwei_faktor
    setError('');
    setLoading(true);
    try {
      await anmelden();
    } catch (err: any) {
      setError(fehlerText(err, 'Anmeldung fehlgeschlagen.'));
      setSchritt('passwort');
    } finally {
      setLoading(false);
    }
  };

  const zurueck = () => {
    setSchritt('passwort');
    setZwischenToken('');
    setCode('');
    setError('');
    setCodeGesendet(null);
  };

  const renderFormular = () => {
    if (schritt === 'einrichten') {
      return (
        <div className="login-form">
          <h1 className="login-h1">Zwei-Faktor einrichten</h1>
          <p className="login-hint">Ihr Unternehmen verlangt eine Zwei-Faktor-Authentifizierung. Bitte richten Sie sie jetzt ein.</p>
          <ZweiFaktorEinrichtung token={zwischenToken} tokenArt="setup" onFertig={nachEinrichtung} />
          {error && <div className="login-error">{error}</div>}
        </div>
      );
    }
    if (schritt === 'code') {
      const hinweis = backupModus
        ? 'Geben Sie einen Ihrer Backup-Codes ein.'
        : methode === 'email'
          ? (codeGesendet === false
              ? "Der Code konnte nicht gesendet werden – bitte 'Code erneut senden' nutzen oder einen Backup-Code eingeben."
              : `Wir haben einen Code an ${emailMaskiert} gesendet.`)
          : 'Geben Sie den Code aus Ihrer Authenticator-App ein.';
      return (
        <form className="login-form" onSubmit={handleCode}>
          <h1 className="login-h1">Bestätigungscode</h1>
          <p className="login-hint">{hinweis}</p>
          <div className="login-field">
            <label>{backupModus ? 'Backup-Code' : 'Code'}</label>
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder={backupModus ? 'xxxxx-xxxxx' : '123456'}
              inputMode={backupModus ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              required
              autoFocus
            />
          </div>
          <label className="login-check">
            <input type="checkbox" checked={geraetMerken} onChange={e => setGeraetMerken(e.target.checked)} />
            Dieses Gerät 30 Tage merken
          </label>
          {error && <div className="login-error">{error}</div>}
          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? 'Prüfe …' : 'Bestätigen'} <span className="arr">→</span>
          </button>
          <div className="login-links">
            <button type="button" className="login-link" onClick={() => setBackupModus(b => !b)}>
              {backupModus ? 'Code aus App/E-Mail verwenden' : 'Backup-Code verwenden'}
            </button>
            {methode === 'email' && !backupModus && (
              <button type="button" className="login-link" onClick={codeErneutSenden} disabled={resendSperre > 0}>
                {resendSperre > 0 ? `Erneut senden (${resendSperre}s)` : 'Code erneut senden'}
              </button>
            )}
            <button type="button" className="login-link" onClick={zurueck}>Zurück</button>
          </div>
        </form>
      );
    }
    return (
      <form className="login-form" onSubmit={handleSubmit}>
        <div className="login-field">
          <label>E-Mail</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="name@firma.de"
            required
            autoFocus
          />
        </div>
        <div className="login-field">
          <label>Passwort</label>
          <input
            type="password"
            value={passwort}
            onChange={e => setPasswort(e.target.value)}
            placeholder="••••••••"
            required
          />
        </div>
        <div className="login-field">
          <label>Firma</label>
          {mandanten.length > 0 ? (
            <select value={tenantSlug} onChange={e => setTenantSlug(e.target.value)}>
              <option value="">— Firma wählen (optional) —</option>
              {mandanten.map(m => (
                <option key={m.slug} value={m.slug}>{m.firmenname}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              placeholder="Firma (optional)"
              value={tenantSlug}
              onChange={e => setTenantSlug(e.target.value)}
            />
          )}
        </div>

        {error && <div className="login-error">{error}</div>}

        <button className="login-btn" type="submit" disabled={loading}>
          {loading ? 'Anmeldung …' : 'Anmelden'} <span className="arr">→</span>
        </button>
        <div className="login-links">
          <a className="login-link" href="/passwort-vergessen">Passwort vergessen?</a>
        </div>
      </form>
    );
  };

  return (
    <div className="login-page">
      <div className="login-inner">
        <div className="login-intro">
          <span className="login-eyebrow">Kontor / Agentisches ERP</span>
          <h1 className="login-h1">Fragen Sie Ihr <em>Unternehmen</em>.</h1>
          <p className="login-lede">
            Keine Masken, keine Menüpfade. Der Agent liest Ihre ERP-Daten,
            rechnet, und legt Ihnen das Ergebnis auf die Bühne.
          </p>
          <div className="login-claim">DSGVO by default — Made in Germany</div>
        </div>

        <div className="login-frame">
          <span className="k-corner k-corner--tl">{'// kontor / zugang'}</span>
          <span className="k-corner k-corner--tr">erp.live</span>
          <span className="k-corner k-corner--bl">verschlüsselt</span>
          <span className="k-corner k-corner--br">mandantengetrennt</span>
          {renderFormular()}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
